import { useEffect, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { updateUserProfile, updateUserPassword, deactivateAccount } from '@/services/users';
import { AvatarPicker } from '@/components/feature/AvatarPicker';

export default function DashboardProfile() {
  const { profile, role, user, refreshProfile, signOut } = useAuth();

  // Profile fields
  const [name, setName]         = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError]   = useState('');
  const [profileSaved, setProfileSaved]   = useState(false);

  // Password fields
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [pwSaving, setPwSaving]     = useState(false);
  const [pwError, setPwError]       = useState('');
  const [pwSaved, setPwSaved]       = useState(false);

  // Media picker
  const [showPicker, setShowPicker] = useState(false);

  // Delete account modal
  const [showDelete, setShowDelete] = useState(false);
  const [deleteAccepted, setDeleteAccepted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    setName(profile?.name ?? user?.email ?? '');
    setAvatarUrl(profile?.avatarUrl ?? '');
  }, [profile, user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSaving(true);
    try {
      await updateUserProfile({ name, avatarUrl });
      await refreshProfile();
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err: unknown) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    if (!currentPw) { setPwError('Enter your current password.'); return; }
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setPwError('New passwords do not match.'); return; }
    setPwSaving(true);
    try {
      await updateUserPassword(currentPw, newPw);
      setPwSaved(true);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err: unknown) {
      setPwError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setPwSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deactivateAccount();
      setShowDelete(false);
      await signOut();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account.');
      setDeleting(false);
    }
  };

  const initials = name ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Profile</h1>
        <p className="text-sm text-surface-500 mt-1">Manage your account settings and preferences.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
        <div className="lg:col-span-2 space-y-5">

          {/* Account Information */}
          <div className="bg-white rounded-xl border border-surface-200 p-5 md:p-6">
            <h2 className="font-semibold text-surface-900 text-sm mb-5">Account Information</h2>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={profile?.email ?? user?.email ?? ''}
                    disabled
                    className="w-full px-3 py-2.5 rounded-lg border border-surface-200 bg-surface-50 text-surface-500 text-sm cursor-not-allowed"
                  />
                  <p className="text-xs text-surface-400 mt-1">Email cannot be changed here.</p>
                </div>
              </div>

              {profileError && (
                <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line" /> {profileError}</p>
              )}

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={profileSaving}
                  className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center gap-2"
                >
                  {profileSaving && <i className="ri-loader-4-line animate-spin" />}
                  {profileSaved ? '✓ Saved!' : profileSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>

          {/* Change Password */}
          <div className="bg-white rounded-xl border border-surface-200 p-5 md:p-6">
            <h2 className="font-semibold text-surface-900 text-sm mb-5">Change Password</h2>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">Current Password</label>
                <input
                  type="password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  placeholder="Enter current password"
                  className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">New Password</label>
                  <input
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Re-enter new password"
                    className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 ${
                      confirmPw && confirmPw !== newPw ? 'border-red-400 bg-red-50' : 'border-surface-300'
                    }`}
                  />
                </div>
              </div>

              {pwError && (
                <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line" /> {pwError}</p>
              )}
              {pwSaved && (
                <p className="text-xs text-emerald-600 flex items-center gap-1"><i className="ri-check-line" /> Password updated successfully.</p>
              )}

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={pwSaving}
                  className="px-5 py-2.5 rounded-lg bg-surface-800 hover:bg-surface-900 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center gap-2"
                >
                  {pwSaving && <i className="ri-loader-4-line animate-spin" />}
                  {pwSaving ? 'Updating…' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-5">
          {/* Avatar card */}
          <div className="bg-white rounded-xl border border-surface-200 p-5 text-center">
            <div className="relative inline-block mb-3">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full object-cover border-2 border-surface-200 mx-auto"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-brand-100 flex items-center justify-center mx-auto border-2 border-surface-200">
                  <span className="text-2xl font-bold text-brand-600">{initials}</span>
                </div>
              )}
              <button
                onClick={() => setShowPicker(true)}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-600 hover:bg-brand-700 flex items-center justify-center shadow-md transition-colors"
                title="Change avatar"
              >
                <i className="ri-camera-line text-white text-xs" />
              </button>
            </div>

            <h3 className="font-semibold text-surface-900">{name || user?.email || 'Account'}</h3>
            <p className="text-sm text-surface-500">{profile?.email || user?.email}</p>
            <p className="text-xs text-surface-400 mt-1 capitalize">{role ?? 'customer'} account</p>

            <button
              onClick={() => setShowPicker(true)}
              className="mt-4 px-4 py-2 rounded-lg border border-surface-300 hover:border-brand-400 hover:bg-brand-50 text-surface-700 hover:text-brand-600 text-sm font-medium transition-colors w-full flex items-center justify-center gap-2"
            >
              <i className="ri-image-line" /> Change Avatar
            </button>

            {avatarUrl && (
              <button
                onClick={() => { setAvatarUrl(''); }}
                className="mt-2 text-xs text-surface-400 hover:text-red-500 transition-colors"
              >
                Remove avatar
              </button>
            )}
          </div>

          {/* Preferences */}
          <div className="bg-white rounded-xl border border-surface-200 p-5">
            <h3 className="font-semibold text-surface-900 text-sm mb-3">Preferences</h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-surface-700">Email notifications</span>
                <input type="checkbox" defaultChecked className="w-4 h-4 accent-brand-600 rounded" />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-surface-700">Product update alerts</span>
                <input type="checkbox" defaultChecked className="w-4 h-4 accent-brand-600 rounded" />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-surface-700">Marketing emails</span>
                <input type="checkbox" className="w-4 h-4 accent-brand-600 rounded" />
              </label>
            </div>
          </div>

          {/* Danger zone — customers only */}
          {role === 'customer' && (
            <div className="bg-red-50 rounded-xl border border-red-200 p-5">
              <h3 className="font-semibold text-red-700 text-sm mb-2">Delete Account</h3>
              <p className="text-xs text-red-600 mb-3">
                Your licenses and active orders will be deactivated. You can reactivate within 3 months by logging back in.
              </p>
              <button
                onClick={() => setShowDelete(true)}
                className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-100 transition-colors w-full">
                Delete Account
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Avatar Picker */}
      {showPicker && (
        <AvatarPicker
          selected={avatarUrl}
          onSelect={(url) => setAvatarUrl(url)}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* Delete Account Modal */}
      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setShowDelete(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-surface-200">
              <h3 className="font-semibold text-surface-900 text-sm">Delete Account</h3>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-red-700">This will:</p>
                <ul className="text-xs text-red-600 space-y-1.5 list-disc list-inside">
                  <li>Deactivate all your licenses</li>
                  <li>Prevent plugin updates and new activations</li>
                  <li>Log you out immediately</li>
                </ul>
                <p className="text-xs text-red-600 font-medium pt-1">
                  You can reactivate within 3 months by logging back in. After 3 months, your data will be permanently removed.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteAccepted}
                  onChange={(e) => setDeleteAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-red-600 rounded shrink-0"
                />
                <span className="text-xs text-surface-600 leading-relaxed">
                  I understand that my licenses will be deactivated and I can reactivate my account within 3 months.
                </span>
              </label>

              <div>
                <label className="block text-xs font-medium text-surface-700 mb-1.5">
                  Type <span className="font-mono font-bold text-red-600">delete</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="type delete"
                  className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>

              {deleteError && (
                <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line" /> {deleteError}</p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-end gap-2 bg-surface-50/50">
              <button
                onClick={() => { setShowDelete(false); setDeleteAccepted(false); setDeleteConfirm(''); setDeleteError(''); }}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={!deleteAccepted || deleteConfirm !== 'delete' || deleting}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors">
                {deleting ? 'Deleting…' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
